// src/pages/MessageCampaign.js
// 訊息發送系統（Mail / LINE）

import React, { useState, useEffect, useRef } from 'react';

export default function MessageCampaign() {
  // === 狀態管理 ===
  const [members, setMembers] = useState([]);
  const [selectedMembers, setSelectedMembers] = useState(new Set());
  const [loading, setLoading] = useState(false);
  
  // 篩選條件
  const [filters, setFilters] = useState({
    name: '',
    level: '',
    birthdayMonth: '',
    daysSinceVisit: '',
    hasEmail: '',
    hasLine: '',
    expired: ''  // '' 全部、'not_expired' 未到期、'expired' 已到期
  });

  // 訊息內容
  const [message, setMessage] = useState({
    title: '',
    content: '',
    imageUrl: '',
    channel: 'line' // 'mail' 或 'line'
  });
  
  // 圖片上傳
  const [imagePreview, setImagePreview] = useState(null);
  const [uploading, setUploading] = useState(false);

  // 預發瀏覽
  const [preview, setPreview] = useState({
    recipientId: '',
    recipientName: '',
    recipientType: '',
    searchText: ''
  });
  const [staffList, setStaffList] = useState([]);
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState(null);
  const [showRecipientDropdown, setShowRecipientDropdown] = useState(false);
  const [recipientSearchResults, setRecipientSearchResults] = useState([]);
  const recipientInputRef = useRef(null);
  const recipientDropdownRef = useRef(null);

  // 發送狀態
  const [sending, setSending] = useState(false);
  const [sendResult, setSendResult] = useState(null);

  // 歷史記錄
  const [showHistory, setShowHistory] = useState(false);
  const [history, setHistory] = useState([]);
  const [selectedCampaign, setSelectedCampaign] = useState(null);

  // 優惠券到期提醒
  const [couponExpiry, setCouponExpiry] = useState({
    withinDays: 7,
    members: [],
    loading: false,
    selected: new Set(),
    sending: false,
    sendResult: null,
    template: '您好~ 目前尚有以下優惠券即將到期：\n{coupons}\n不要忘了兌換呦~',
  });
  const [couponHolder, setCouponHolder] = useState({
    couponOptions: [],
    couponId: '',
    holders: [],
    loading: false,
    selected: new Set(),
    sending: false,
    sendResult: null,
    template: '您好~ 你持有以下未使用優惠券：\n{coupons}\n歡迎盡快兌換呦~',
  });
  const [narrow, setNarrow] = useState(() => typeof window !== 'undefined' && window.innerWidth < 768);
  useEffect(() => {
    const onR = () => setNarrow(window.innerWidth < 768);
    window.addEventListener('resize', onR);
    return () => window.removeEventListener('resize', onR);
  }, []);

  // === 載入會員列表 ===
  const loadMembers = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      Object.entries(filters).forEach(([key, value]) => {
        if (value) params.append(key, value);
      });

      const res = await fetch(`/api/message-campaign/members?${params}`, { credentials: 'include' });
      const ct = (res.headers.get('content-type') || '').toLowerCase();
      if (!ct.includes('application/json')) {
        const text = await res.text();
        throw new Error(`non_json_response status=${res.status} ct=${ct} head=${text.slice(0, 80)}`);
      }
      const data = await res.json();
      if (data.success) {
        setMembers(data.members);
      } else {
        alert('載入會員失敗: ' + data.error);
      }
    } catch (err) {
      console.error(err);
      alert('載入會員失敗');
    } finally {
      setLoading(false);
    }
  };

  // 即時篩選：當 filters 改變時自動重新載入
  useEffect(() => {
    const timer = setTimeout(() => {
      loadMembers();
    }, 300); // 防抖：300ms 延遲
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters]);

  // === 載入員工列表 ===
  const loadStaff = async (search = '') => {
    try {
      const params = new URLSearchParams();
      if (search) params.append('search', search);

      const res = await fetch(`/api/message-campaign/staff?${params}`, { credentials: 'include' });
      const ct = (res.headers.get('content-type') || '').toLowerCase();
      if (!ct.includes('application/json')) {
        const text = await res.text();
        throw new Error(`non_json_response status=${res.status} ct=${ct} head=${text.slice(0, 80)}`);
      }
      const data = await res.json();
      if (data.success) {
        setStaffList(data.staff);
      }
    } catch (err) {
      console.error(err);
    }
  };

  useEffect(() => {
    loadStaff();
  }, []);

  useEffect(() => {
    const loadCouponOptions = async () => {
      try {
        const adminToken = localStorage.getItem('admin_token');
        const res = await fetch('/api/admin/coupons', {
          credentials: 'include',
          headers: adminToken ? { Authorization: `Bearer ${adminToken}` } : undefined,
        });
        const ct = (res.headers.get('content-type') || '').toLowerCase();
        if (!ct.includes('application/json')) {
          const text = await res.text();
          throw new Error(`non_json_response status=${res.status} ct=${ct} head=${text.slice(0, 80)}`);
        }
        const data = await res.json();
        const rows = Array.isArray(data?.rows) ? data.rows : [];
        setCouponHolder((p) => ({ ...p, couponOptions: rows }));
      } catch (err) {
        console.error('load coupon options failed:', err);
      }
    };
    loadCouponOptions();
  }, []);

  // 搜尋接收者（員工或會員）- 模糊搜尋
  const searchRecipient = (text) => {
    setPreview({ ...preview, searchText: text });
    if (!text) {
      setRecipientSearchResults([]);
      setShowRecipientDropdown(false);
      return;
    }

    const results = [];
    const lowerText = text.toLowerCase();
    
    // 搜尋員工
    staffList.forEach(staff => {
      if (staff.NAME.toLowerCase().includes(lowerText) || 
          staff.ID.toString().includes(lowerText)) {
        results.push({ ...staff, type: 'staff' });
      }
    });
    
    // 搜尋會員
    members.forEach(member => {
      if (member.NAME.toLowerCase().includes(lowerText) || 
          member.ID.toString().includes(lowerText) ||
          (member.PHONE && member.PHONE.includes(lowerText))) {
        results.push({ ...member, type: 'member' });
      }
    });

    setRecipientSearchResults(results);
    setShowRecipientDropdown(results.length > 0);
  };

  const selectRecipient = (recipient) => {
    setPreview({
      ...preview,
      recipientId: recipient.ID.toString(),
      recipientName: recipient.NAME,
      recipientType: recipient.type,
      searchText: recipient.NAME
    });
    setShowRecipientDropdown(false);
  };

  // 點擊外部關閉下拉選單
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (recipientDropdownRef.current && 
          !recipientDropdownRef.current.contains(event.target) &&
          recipientInputRef.current &&
          !recipientInputRef.current.contains(event.target)) {
        setShowRecipientDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // === 全選/取消全選 ===
  const toggleSelectAll = () => {
    if (selectedMembers.size === members.length) {
      setSelectedMembers(new Set());
    } else {
      setSelectedMembers(new Set(members.map(m => m.ID)));
    }
  };

  // === 切換單一會員選擇 ===
  const toggleMember = (memberId) => {
    const newSet = new Set(selectedMembers);
    if (newSet.has(memberId)) {
      newSet.delete(memberId);
    } else {
      newSet.add(memberId);
    }
    setSelectedMembers(newSet);
  };

  // 取得已選取的會員資訊
  const getSelectedMembersInfo = () => {
    return members.filter(m => selectedMembers.has(m.ID));
  };

  // === 測試發送 ===
  const handleTestSend = async () => {
    if (!message.content) {
      alert('請輸入訊息內容');
      return;
    }

    if (!preview.recipientId) {
      alert('請選擇預發對象');
      return;
    }

    setTesting(true);
    setTestResult(null);

    try {
      const res = await fetch('/api/message-campaign/test', {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          content: message.content,
          imageUrl: message.imageUrl || null,
          channel: message.channel,
          recipientId: parseInt(preview.recipientId),
          recipientType: preview.recipientType
        })
      });

      const ct = (res.headers.get('content-type') || '').toLowerCase();
      if (!ct.includes('application/json')) {
        const text = await res.text();
        throw new Error(`non_json_response status=${res.status} ct=${ct} head=${text.slice(0, 80)}`);
      }
      const data = await res.json();
      setTestResult(data);
      
      if (data.success) {
        alert('測試發送成功！');
      } else {
        alert('測試發送失敗: ' + (data.error || '未知錯誤'));
      }
    } catch (err) {
      console.error(err);
      alert('測試發送失敗');
    } finally {
      setTesting(false);
    }
  };

  // === 正式發送 ===
  const handleSend = async () => {
    if (!message.content) {
      alert('請輸入訊息內容');
      return;
    }

    if (selectedMembers.size === 0) {
      alert('請至少選擇一個會員');
      return;
    }

    if (!window.confirm(`確定要發送訊息給 ${selectedMembers.size} 位會員嗎？`)) {
      return;
    }

    setSending(true);
    setSendResult(null);

    try {
      const res = await fetch('/api/message-campaign/send', {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          title: message.title || null,
          content: message.content,
          imageUrl: message.imageUrl || null,
          channel: message.channel,
          memberIds: Array.from(selectedMembers),
          senderId: null,
          senderName: null
        })
      });

      const ct = (res.headers.get('content-type') || '').toLowerCase();
      if (!ct.includes('application/json')) {
        const text = await res.text();
        throw new Error(`non_json_response status=${res.status} ct=${ct} head=${text.slice(0, 80)}`);
      }
      const data = await res.json();
      setSendResult(data);

      if (data.success) {
        alert(`發送完成！成功: ${data.successCount}, 失敗: ${data.failCount}`);
        // 清空選擇
        setSelectedMembers(new Set());
        setMessage({ ...message, content: '', imageUrl: '', title: '' });
        setImagePreview(null);
      } else {
        alert('發送失敗: ' + (data.error || '未知錯誤'));
      }
    } catch (err) {
      console.error(err);
      alert('發送失敗');
    } finally {
      setSending(false);
    }
  };

  // === 處理圖片上傳（檔案選擇） ===
  const handleImageUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;
    await processImageFile(file);
  };

  // === 處理拖放上傳 ===
  const handleDrop = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    
    const file = e.dataTransfer.files[0];
    if (file) {
      await processImageFile(file);
    }
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  // === 處理圖片檔案（共用邏輯） ===
  const processImageFile = async (file) => {
    if (!file.type.startsWith('image/')) {
      alert('請選擇圖片檔案');
      return;
    }

    if (file.size > 10 * 1024 * 1024) {
      alert('圖片大小不能超過 10MB');
      return;
    }

    const base64 = await fileToBase64(file);
    setImagePreview(base64);

    setUploading(true);
    try {
      const res = await fetch('/api/message-campaign/upload-image', {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ file: base64 })
      });

      const ct = (res.headers.get('content-type') || '').toLowerCase();
      if (!ct.includes('application/json')) {
        const text = await res.text();
        throw new Error(`non_json_response status=${res.status} ct=${ct} head=${text.slice(0, 80)}`);
      }
      const data = await res.json();
      if (data.success) {
        setMessage({ ...message, imageUrl: data.imageUrl });
        // 如果 URL 驗證失敗，顯示警告
        if (data.urlValid === false) {
          alert('⚠️ 圖片已上傳，但 URL 可能無法從外部訪問。\n\n如果 LINE 訊息顯示假圖示，請檢查：\n1. 伺服器是否配置 HTTPS\n2. 圖片 URL 是否可公開訪問\n3. 防火牆或反向代理設定');
        }
      } else {
        alert('圖片上傳失敗: ' + (data.error || '未知錯誤'));
        setImagePreview(null);
      }
    } catch (err) {
      console.error(err);
      alert('圖片上傳失敗');
      setImagePreview(null);
    } finally {
      setUploading(false);
    }
  };

  const fileToBase64 = (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result);
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });
  };

  const handleRemoveImage = () => {
    setImagePreview(null);
    setMessage({ ...message, imageUrl: '' });
  };

  // === 載入歷史記錄 ===
  const loadHistory = async () => {
    try {
      const res = await fetch('/api/message-campaign/history?limit=50', { credentials: 'include' });
      const ct = (res.headers.get('content-type') || '').toLowerCase();
      if (!ct.includes('application/json')) {
        const text = await res.text();
        throw new Error(`non_json_response status=${res.status} ct=${ct} head=${text.slice(0, 80)}`);
      }
      const data = await res.json();
      if (data.success) {
        setHistory(data.campaigns);
      }
    } catch (err) {
      console.error(err);
    }
  };

  const viewCampaignDetail = async (campaignId) => {
    try {
      const res = await fetch(`/api/message-campaign/history/${campaignId}`, { credentials: 'include' });
      const ct = (res.headers.get('content-type') || '').toLowerCase();
      if (!ct.includes('application/json')) {
        const text = await res.text();
        throw new Error(`non_json_response status=${res.status} ct=${ct} head=${text.slice(0, 80)}`);
      }
      const data = await res.json();
      if (data.success) {
        setSelectedCampaign(data);
      }
    } catch (err) {
      console.error(err);
    }
  };

  // === 優惠券到期提醒 ===
  const loadCouponExpiry = async () => {
    setCouponExpiry(p => ({ ...p, loading: true, members: [], selected: new Set(), sendResult: null }));
    try {
      const res = await fetch(`/api/message-campaign/coupon-expiry?withinDays=${couponExpiry.withinDays}`, { credentials: 'include' });
      const ct = (res.headers.get('content-type') || '').toLowerCase();
      if (!ct.includes('application/json')) {
        const text = await res.text();
        throw new Error(`non_json_response status=${res.status} ct=${ct} head=${text.slice(0, 80)}`);
      }
      const data = await res.json();
      if (data.success) {
        setCouponExpiry(p => ({
          ...p,
          loading: false,
          members: data.members,
          selected: new Set(data.members.map(m => m.MEMBER_ID)),
        }));
      } else {
        alert('查詢失敗: ' + data.error);
        setCouponExpiry(p => ({ ...p, loading: false }));
      }
    } catch (err) {
      console.error(err);
      alert('查詢失敗');
      setCouponExpiry(p => ({ ...p, loading: false }));
    }
  };

  const toggleCouponMember = (memberId) => {
    setCouponExpiry(p => {
      const s = new Set(p.selected);
      if (s.has(memberId)) s.delete(memberId); else s.add(memberId);
      return { ...p, selected: s };
    });
  };

  const toggleCouponAll = () => {
    setCouponExpiry(p => {
      if (p.selected.size === p.members.length) {
        return { ...p, selected: new Set() };
      }
      return { ...p, selected: new Set(p.members.map(m => m.MEMBER_ID)) };
    });
  };

  const sendCouponReminder = async () => {
    if (couponExpiry.selected.size === 0) { alert('請選擇會員'); return; }
    if (!window.confirm(`確定要發送優惠券到期提醒給 ${couponExpiry.selected.size} 位會員嗎？`)) return;

    setCouponExpiry(p => ({ ...p, sending: true, sendResult: null }));
    try {
      const res = await fetch('/api/message-campaign/coupon-expiry/send', {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          memberIds: Array.from(couponExpiry.selected),
          template: couponExpiry.template,
        }),
      });
      const ct = (res.headers.get('content-type') || '').toLowerCase();
      if (!ct.includes('application/json')) {
        const text = await res.text();
        throw new Error(`non_json_response status=${res.status} ct=${ct} head=${text.slice(0, 80)}`);
      }
      const data = await res.json();
      setCouponExpiry(p => ({ ...p, sending: false, sendResult: data }));
      if (data.success) {
        alert(`發送完成！成功: ${data.successCount}, 失敗: ${data.failCount}`);
      } else {
        alert('發送失敗: ' + (data.error || '未知錯誤'));
      }
    } catch (err) {
      console.error(err);
      setCouponExpiry(p => ({ ...p, sending: false }));
      alert('發送失敗');
    }
  };

  const loadCouponHolders = async () => {
    if (!couponHolder.couponId) { alert('請先選擇優惠券'); return; }
    setCouponHolder((p) => ({ ...p, loading: true, holders: [], selected: new Set(), sendResult: null }));
    try {
      const res = await fetch(`/api/message-campaign/coupon-holders?couponId=${encodeURIComponent(couponHolder.couponId)}`, { credentials: 'include' });
      const ct = (res.headers.get('content-type') || '').toLowerCase();
      if (!ct.includes('application/json')) {
        const text = await res.text();
        throw new Error(`non_json_response status=${res.status} ct=${ct} head=${text.slice(0, 80)}`);
      }
      const data = await res.json();
      if (data.success) {
        const holders = Array.isArray(data.holders) ? data.holders : [];
        setCouponHolder((p) => ({
          ...p,
          loading: false,
          holders,
          selected: new Set(holders.map((h) => h.MEMBER_ID)),
        }));
      } else {
        alert('查詢失敗: ' + (data.error || '未知錯誤'));
        setCouponHolder((p) => ({ ...p, loading: false }));
      }
    } catch (err) {
      console.error(err);
      alert('查詢失敗');
      setCouponHolder((p) => ({ ...p, loading: false }));
    }
  };

  const toggleCouponHolderMember = (memberId) => {
    setCouponHolder((p) => {
      const s = new Set(p.selected);
      if (s.has(memberId)) s.delete(memberId); else s.add(memberId);
      return { ...p, selected: s };
    });
  };

  const toggleCouponHolderAll = () => {
    setCouponHolder((p) => {
      if (p.selected.size === p.holders.length) return { ...p, selected: new Set() };
      return { ...p, selected: new Set(p.holders.map((h) => h.MEMBER_ID)) };
    });
  };

  const sendCouponHolderReminder = async () => {
    if (!couponHolder.couponId) { alert('請先選擇優惠券'); return; }
    if (couponHolder.selected.size === 0) { alert('請選擇會員'); return; }
    if (!window.confirm(`確定要發送未使用優惠券提醒給 ${couponHolder.selected.size} 位會員嗎？`)) return;

    setCouponHolder((p) => ({ ...p, sending: true, sendResult: null }));
    try {
      const res = await fetch('/api/message-campaign/coupon-holders/send', {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          couponId: Number(couponHolder.couponId),
          memberIds: Array.from(couponHolder.selected),
          template: couponHolder.template,
        }),
      });
      const ct = (res.headers.get('content-type') || '').toLowerCase();
      if (!ct.includes('application/json')) {
        const text = await res.text();
        throw new Error(`non_json_response status=${res.status} ct=${ct} head=${text.slice(0, 80)}`);
      }
      const data = await res.json();
      setCouponHolder((p) => ({ ...p, sending: false, sendResult: data }));
      if (data.success) {
        alert(`發送完成！成功: ${data.successCount}, 失敗: ${data.failCount}`);
      } else {
        alert('發送失敗: ' + (data.error || '未知錯誤'));
      }
    } catch (err) {
      console.error(err);
      setCouponHolder((p) => ({ ...p, sending: false }));
      alert('發送失敗');
    }
  };

  const selectedMembersInfo = getSelectedMembersInfo();

  return (
    <div style={{
      display: 'flex',
      flexDirection: narrow ? 'column' : 'row',
      height: narrow ? 'auto' : '100%',
      minHeight: narrow ? '100%' : 0,
      backgroundColor: '#f5f7fa',
      fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
      overflow: narrow ? 'visible' : 'hidden',
      maxWidth: '100%'
    }}>
      {/* 左側：會員選擇 */}
      <div style={{
        width: narrow ? '100%' : '380px',
        maxWidth: '100%',
        flex: narrow ? '0 0 auto' : undefined,
        maxHeight: narrow ? '50vh' : undefined,
        backgroundColor: '#fff',
        borderRight: narrow ? 'none' : '1px solid #e1e8ed',
        borderBottom: narrow ? '1px solid #e1e8ed' : 'none',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
        boxShadow: '2px 0 8px rgba(0,0,0,0.04)'
      }}>
        {/* 標題與全選 */}
        <div style={{
          padding: '20px',
          borderBottom: '1px solid #e1e8ed',
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          color: '#fff'
        }}>
          <h3 style={{ margin: '0 0 16px 0', fontSize: '20px', fontWeight: '700', color: '#fff' }}>
            ✨ 選擇會員
          </h3>
          <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
            <button
              onClick={toggleSelectAll}
              style={{
                flex: 1,
                padding: '12px 18px',
                background: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
                color: '#fff',
                border: 'none',
                borderRadius: '8px',
                fontSize: '16px',
                fontWeight: '600',
                cursor: 'pointer',
                transition: 'all 0.2s',
                boxShadow: '0 4px 12px rgba(245, 87, 108, 0.3)'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = 'translateY(-2px)';
                e.currentTarget.style.boxShadow = '0 6px 16px rgba(245, 87, 108, 0.4)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.boxShadow = '0 4px 12px rgba(245, 87, 108, 0.3)';
              }}
            >
              {selectedMembers.size === members.length ? '取消全選' : '全選'}
            </button>
            <div style={{
              padding: '10px 14px',
              background: 'linear-gradient(135deg, #ffeaa7 0%, #fdcb6e 100%)',
              borderRadius: '8px',
              fontSize: '16px',
              fontWeight: '700',
              color: '#8b6914',
              minWidth: '80px',
              textAlign: 'center',
              boxShadow: '0 2px 8px rgba(253, 203, 110, 0.3)'
            }}>
              {selectedMembers.size} 人
            </div>
          </div>
        </div>

        {/* 篩選條件 */}
        <div style={{
          padding: '20px',
          borderBottom: '1px solid #e1e8ed',
          maxHeight: narrow ? '160px' : '320px',
          overflowY: 'auto',
          background: 'linear-gradient(180deg, #f8f9ff 0%, #ffffff 100%)'
        }}>
          <div style={{ marginBottom: '16px', fontSize: '17px', fontWeight: '700', color: '#4a5568' }}>
            🔍 篩選條件
          </div>
          
          <div style={{ display: 'grid', gap: '12px' }}>
            <div>
              <label style={{ display: 'block', marginBottom: '6px', fontSize: '15px', color: '#4a5568', fontWeight: '600' }}>
                名字搜尋
              </label>
              <input
                type="text"
                value={filters.name}
                onChange={(e) => setFilters({ ...filters, name: e.target.value })}
                placeholder="輸入名字..."
                style={{
                  width: '100%',
                  padding: '10px 14px',
                  border: '1px solid #d1d9e0',
                  borderRadius: '6px',
                  fontSize: '15px',
                  boxSizing: 'border-box',
                  transition: 'border-color 0.2s'
                }}
                onFocus={(e) => e.target.style.borderColor = '#4a90e2'}
                onBlur={(e) => e.target.style.borderColor = '#d1d9e0'}
              />
            </div>

            <div>
              <label style={{ display: 'block', marginBottom: '6px', fontSize: '15px', color: '#4a5568', fontWeight: '600' }}>
                等級
              </label>
              <select
                value={filters.level}
                onChange={(e) => setFilters({ ...filters, level: e.target.value })}
                style={{
                  width: '100%',
                  padding: '10px 14px',
                  border: '1px solid #d1d9e0',
                  borderRadius: '6px',
                  fontSize: '15px',
                  backgroundColor: '#fff',
                  cursor: 'pointer',
                  transition: 'border-color 0.2s'
                }}
                onFocus={(e) => e.target.style.borderColor = '#4a90e2'}
                onBlur={(e) => e.target.style.borderColor = '#d1d9e0'}
              >
                <option value="">全部</option>
                <option value="年會員">年會員</option>
                <option value="資深年會員">資深年會員</option>
                <option value="月會員">月會員</option>
                <option value="一般顧客">一般顧客</option>
              </select>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
              <div>
                <label style={{ display: 'block', marginBottom: '6px', fontSize: '15px', color: '#4a5568', fontWeight: '600' }}>
                  生日月份
                </label>
                <select
                  value={filters.birthdayMonth}
                  onChange={(e) => setFilters({ ...filters, birthdayMonth: e.target.value })}
                  style={{
                    width: '100%',
                    padding: '10px 14px',
                    border: '1px solid #d1d9e0',
                    borderRadius: '6px',
                    fontSize: '15px',
                    backgroundColor: '#fff',
                    cursor: 'pointer',
                    transition: 'border-color 0.2s'
                  }}
                  onFocus={(e) => e.target.style.borderColor = '#4a90e2'}
                  onBlur={(e) => e.target.style.borderColor = '#d1d9e0'}
                >
                  <option value="">全部</option>
                  {Array.from({ length: 12 }, (_, i) => i + 1).map(m => (
                    <option key={m} value={m}>{m}月</option>
                  ))}
                </select>
              </div>

              <div>
                <label style={{ display: 'block', marginBottom: '6px', fontSize: '15px', color: '#4a5568', fontWeight: '600' }}>
                  幾天沒來
                </label>
                <input
                  type="number"
                  value={filters.daysSinceVisit}
                  onChange={(e) => setFilters({ ...filters, daysSinceVisit: e.target.value })}
                  placeholder="天數"
                  style={{
                    width: '100%',
                    padding: '10px 14px',
                    border: '1px solid #d1d9e0',
                    borderRadius: '6px',
                    fontSize: '15px',
                    boxSizing: 'border-box',
                    transition: 'border-color 0.2s'
                  }}
                  onFocus={(e) => e.target.style.borderColor = '#4a90e2'}
                  onBlur={(e) => e.target.style.borderColor = '#d1d9e0'}
                />
              </div>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
              <div>
                <label style={{ display: 'block', marginBottom: '6px', fontSize: '15px', color: '#4a5568', fontWeight: '600' }}>
                  📧 有 Email
                </label>
                <select
                  value={filters.hasEmail}
                  onChange={(e) => setFilters({ ...filters, hasEmail: e.target.value })}
                  style={{
                    width: '100%',
                    padding: '10px 14px',
                    border: '1px solid #d1d9e0',
                    borderRadius: '6px',
                    fontSize: '15px',
                    backgroundColor: '#fff',
                    cursor: 'pointer',
                    transition: 'border-color 0.2s'
                  }}
                  onFocus={(e) => e.target.style.borderColor = '#4a90e2'}
                  onBlur={(e) => e.target.style.borderColor = '#d1d9e0'}
                >
                  <option value="">全部</option>
                  <option value="true">是</option>
                  <option value="false">否</option>
                </select>
              </div>

              <div>
                <label style={{ display: 'block', marginBottom: '6px', fontSize: '15px', color: '#4a5568', fontWeight: '600' }}>
                  💬 有 LINE
                </label>
                <select
                  value={filters.hasLine}
                  onChange={(e) => setFilters({ ...filters, hasLine: e.target.value })}
                  style={{
                    width: '100%',
                    padding: '10px 14px',
                    border: '1px solid #d1d9e0',
                    borderRadius: '6px',
                    fontSize: '15px',
                    backgroundColor: '#fff',
                    cursor: 'pointer',
                    transition: 'border-color 0.2s'
                  }}
                  onFocus={(e) => e.target.style.borderColor = '#4a90e2'}
                  onBlur={(e) => e.target.style.borderColor = '#d1d9e0'}
                >
                  <option value="">全部</option>
                  <option value="true">是</option>
                  <option value="false">否</option>
                </select>
              </div>
            </div>

            <div>
              <label style={{ display: 'block', marginBottom: '6px', fontSize: '15px', color: '#4a5568', fontWeight: '600' }}>
                📅 是否已到期
              </label>
              <select
                value={filters.expired}
                onChange={(e) => setFilters({ ...filters, expired: e.target.value })}
                style={{
                  width: '100%',
                  padding: '10px 14px',
                  border: '1px solid #d1d9e0',
                  borderRadius: '6px',
                  fontSize: '15px',
                  backgroundColor: '#fff',
                  cursor: 'pointer',
                  transition: 'border-color 0.2s'
                }}
                onFocus={(e) => e.target.style.borderColor = '#4a90e2'}
                onBlur={(e) => e.target.style.borderColor = '#d1d9e0'}
              >
                <option value="">全部</option>
                <option value="not_expired">未到期</option>
                <option value="expired">已到期</option>
              </select>
            </div>
          </div>

          {loading && (
            <div style={{ marginTop: '12px', textAlign: 'center', color: '#8a9ba8', fontSize: '13px' }}>
              載入中...
            </div>
          )}
        </div>

        {/* 已選取的會員 */}
        {selectedMembersInfo.length > 0 && (
          <div style={{
            padding: '18px 20px',
            borderBottom: '1px solid #e1e8ed',
            background: 'linear-gradient(135deg, #a8edea 0%, #fed6e3 100%)',
            maxHeight: '200px',
            overflowY: 'auto'
          }}>
            <div style={{ fontSize: '16px', fontWeight: '700', color: '#2d3748', marginBottom: '14px' }}>
              ✅ 已選取的會員 ({selectedMembersInfo.length})
            </div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
              {selectedMembersInfo.map(member => (
                <div
                  key={member.ID}
                  onClick={() => toggleMember(member.ID)}
                  style={{
                    padding: '8px 14px',
                    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                    color: '#fff',
                    borderRadius: '20px',
                    fontSize: '14px',
                    fontWeight: '600',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '6px',
                    transition: 'all 0.2s',
                    boxShadow: '0 2px 6px rgba(102, 126, 234, 0.3)'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.transform = 'scale(1.05)';
                    e.currentTarget.style.boxShadow = '0 4px 10px rgba(102, 126, 234, 0.4)';
                    e.currentTarget.innerHTML = `${member.NAME} ×`;
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.transform = 'scale(1)';
                    e.currentTarget.style.boxShadow = '0 2px 6px rgba(102, 126, 234, 0.3)';
                    e.currentTarget.innerHTML = member.NAME;
                  }}
                >
                  {member.NAME}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* 會員列表 */}
        <div style={{
          flex: 1,
          minHeight: 0,
          overflowY: 'auto',
          padding: '12px'
        }}>
          {members.map(member => (
            <div
              key={member.ID}
              onClick={() => toggleMember(member.ID)}
              style={{
                display: 'flex',
                alignItems: 'center',
                padding: '12px',
                marginBottom: '8px',
                backgroundColor: selectedMembers.has(member.ID) ? '#e8f4f8' : '#fff',
                border: `1px solid ${selectedMembers.has(member.ID) ? '#4a90e2' : '#e1e8ed'}`,
                borderRadius: '8px',
                cursor: 'pointer',
                transition: 'all 0.2s'
              }}
              onMouseEnter={(e) => {
                if (!selectedMembers.has(member.ID)) {
                  e.currentTarget.style.borderColor = '#4a90e2';
                  e.currentTarget.style.backgroundColor = '#f8fbff';
                }
              }}
              onMouseLeave={(e) => {
                if (!selectedMembers.has(member.ID)) {
                  e.currentTarget.style.borderColor = '#e1e8ed';
                  e.currentTarget.style.backgroundColor = '#fff';
                }
              }}
            >
              <input
                type="checkbox"
                checked={selectedMembers.has(member.ID)}
                onChange={() => toggleMember(member.ID)}
                style={{ marginRight: '12px', cursor: 'pointer' }}
              />
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontWeight: '600', fontSize: '15px', color: '#1a1a1a', marginBottom: '6px' }}>
                  {member.NAME}
                </div>
                <div style={{ fontSize: '13px', color: '#6b7280', display: 'flex', gap: '10px', flexWrap: 'wrap', alignItems: 'center' }}>
                  <span style={{
                    padding: '4px 10px',
                    background: (member.DISPLAY_LEVEL || member.LEVEL) === '資深年會員'
                      ? 'linear-gradient(135deg, #fef3c7 0%, #fde68a 100%)'
                      : (member.DISPLAY_LEVEL || member.LEVEL) === '年會員'
                        ? 'linear-gradient(135deg, #dbeafe 0%, #bfdbfe 100%)'
                        : (member.DISPLAY_LEVEL || member.LEVEL) === '月會員'
                          ? 'linear-gradient(135deg, #e0e7ff 0%, #c7d2fe 100%)'
                          : 'linear-gradient(135deg, #f3f4f6 0%, #e5e7eb 100%)',
                    color: (member.DISPLAY_LEVEL || member.LEVEL) === '資深年會員'
                      ? '#92400e'
                      : (member.DISPLAY_LEVEL || member.LEVEL) === '年會員'
                        ? '#1e40af'
                        : (member.DISPLAY_LEVEL || member.LEVEL) === '月會員'
                          ? '#4338ca'
                          : '#4b5563',
                    borderRadius: '14px',
                    fontWeight: '700',
                    fontSize: '13px',
                    boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
                  }}>
                    {member.DISPLAY_LEVEL || member.LEVEL}
                  </span>
                  {member.HAS_EMAIL && <span style={{ fontSize: '14px' }}>📧</span>}
                  {member.HAS_LINE && <span style={{ fontSize: '14px' }}>💬</span>}
                  {member.DAYS_SINCE_VISIT !== null && (
                    <span style={{ color: member.DAYS_SINCE_VISIT > 30 ? '#dc2626' : '#6b7280' }}>
                      {member.DAYS_SINCE_VISIT}天沒來
                    </span>
                  )}
                  {member.DAYS_SINCE_VISIT === null && <span style={{ color: '#9ca3af' }}>從未到訪</span>}
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* 右側：訊息編輯 */}
      <div style={{
        flex: 1,
        width: narrow ? '100%' : undefined,
        maxWidth: '100%',
        minWidth: 0,
        display: 'flex',
        flexDirection: 'column',
        padding: narrow ? '16px' : '24px',
        overflowY: narrow ? 'visible' : 'auto',
        overflowX: narrow ? 'visible' : 'hidden',
        backgroundColor: '#f5f7fa',
        minHeight: 0,
        boxSizing: 'border-box'
      }}>
        {/* 標題與歷史按鈕 */}
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: '24px'
        }}>
          <h2 style={{ 
            margin: 0, 
            fontSize: '28px', 
            fontWeight: '700', 
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            backgroundClip: 'text'
          }}>
            📨 訊息發送系統
          </h2>
          <button
            onClick={() => {
              setShowHistory(!showHistory);
              if (!showHistory) loadHistory();
            }}
            style={{
              padding: '10px 20px',
              backgroundColor: '#6c757d',
              color: '#fff',
              border: 'none',
              borderRadius: '6px',
              fontSize: '14px',
              fontWeight: '500',
              cursor: 'pointer',
              transition: 'all 0.2s'
            }}
            onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#5a6268'}
            onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#6c757d'}
          >
            {showHistory ? '返回編輯' : '查看歷史'}
          </button>
        </div>

        {!showHistory ? (
          <div style={{ 
            display: 'flex', 
            flexDirection: 'column', 
            gap: '20px', 
            maxWidth: '900px',
            paddingBottom: '40px' // 確保底部按鈕有足夠空間
          }}>
            {/* 訊息內容 */}
            <div style={{
              background: 'linear-gradient(135deg, #ffffff 0%, #f8f9ff 100%)',
              padding: '28px',
              borderRadius: '16px',
              boxShadow: '0 4px 16px rgba(102, 126, 234, 0.1)',
              border: '2px solid #e0e7ff'
            }}>
              <div style={{ 
                fontSize: '20px', 
                fontWeight: '700', 
                color: '#4c51bf',
                marginBottom: '24px',
                display: 'flex',
                alignItems: 'center',
                gap: '8px'
              }}>
                ✉️ 訊息內容
              </div>
              
              {message.channel === 'mail' && (
                <div style={{ marginBottom: '24px' }}>
                  <label style={{ display: 'block', marginBottom: '10px', fontSize: '16px', fontWeight: '600', color: '#4a5568' }}>
                    📝 標題（選填）
                  </label>
                  <input
                    type="text"
                    value={message.title}
                    onChange={(e) => setMessage({ ...message, title: e.target.value })}
                    placeholder="輸入標題..."
                    style={{
                      width: '100%',
                      padding: '12px 16px',
                      border: '2px solid #d1d9e0',
                      borderRadius: '8px',
                      fontSize: '16px',
                      boxSizing: 'border-box',
                      transition: 'border-color 0.2s'
                    }}
                    onFocus={(e) => e.target.style.borderColor = '#667eea'}
                    onBlur={(e) => e.target.style.borderColor = '#d1d9e0'}
                  />
                </div>
              )}

              <div style={{ marginBottom: '24px' }}>
                <label style={{ display: 'block', marginBottom: '10px', fontSize: '16px', fontWeight: '600', color: '#4a5568' }}>
                  💬 訊息內容 *
                </label>
                <textarea
                  value={message.content}
                  onChange={(e) => setMessage({ ...message, content: e.target.value })}
                  placeholder="輸入訊息內容..."
                  style={{
                    width: '100%',
                    minHeight: '140px',
                    padding: '14px',
                    border: '2px solid #d1d9e0',
                    borderRadius: '8px',
                    fontSize: '16px',
                    fontFamily: 'inherit',
                    resize: 'vertical',
                    boxSizing: 'border-box',
                    transition: 'border-color 0.2s',
                    lineHeight: '1.6'
                  }}
                  onFocus={(e) => e.target.style.borderColor = '#667eea'}
                  onBlur={(e) => e.target.style.borderColor = '#d1d9e0'}
                />
              </div>

              <div style={{ marginBottom: '24px' }}>
                <label style={{ display: 'block', marginBottom: '10px', fontSize: '16px', fontWeight: '600', color: '#4a5568' }}>
                  🖼️ 圖片上傳（選填）
                </label>
                <div
                  onDrop={handleDrop}
                  onDragOver={handleDragOver}
                  style={{
                    border: '2px dashed #d1d9e0',
                    borderRadius: '8px',
                    padding: '24px',
                    textAlign: 'center',
                    backgroundColor: '#fafbfc',
                    cursor: 'pointer',
                    transition: 'all 0.2s',
                    minHeight: '150px',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    position: 'relative'
                  }}
                  onMouseEnter={(e) => e.currentTarget.style.borderColor = '#4a90e2'}
                  onMouseLeave={(e) => e.currentTarget.style.borderColor = '#d1d9e0'}
                >
                  {imagePreview ? (
                    <div style={{ position: 'relative', width: '100%' }}>
                      <img
                        src={imagePreview}
                        alt="預覽"
                        style={{
                          maxWidth: '100%',
                          maxHeight: '300px',
                          borderRadius: '6px',
                          boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
                        }}
                      />
                      <button
                        onClick={handleRemoveImage}
                        style={{
                          position: 'absolute',
                          top: '8px',
                          right: '8px',
                          backgroundColor: 'rgba(220,53,69,0.9)',
                          color: '#fff',
                          border: 'none',
                          borderRadius: '50%',
                          width: '32px',
                          height: '32px',
                          cursor: 'pointer',
                          fontSize: '18px',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          transition: 'all 0.2s'
                        }}
                        onMouseEnter={(e) => e.currentTarget.style.backgroundColor = 'rgba(200,35,51,0.95)'}
                        onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'rgba(220,53,69,0.9)'}
                      >
                        ×
                      </button>
                    </div>
                  ) : (
                    <>
                      <div style={{ fontSize: '48px', marginBottom: '12px' }}>📷</div>
                      <div style={{ fontSize: '14px', color: '#8a9ba8', marginBottom: '12px' }}>
                        拖放圖片到這裡，或點擊選擇檔案
                      </div>
                      <input
                        type="file"
                        accept="image/*"
                        onChange={handleImageUpload}
                        style={{ display: 'none' }}
                        id="image-upload-input"
                      />
                      <label
                        htmlFor="image-upload-input"
                        style={{
                          padding: '10px 20px',
                          backgroundColor: '#4a90e2',
                          color: '#fff',
                          borderRadius: '6px',
                          cursor: 'pointer',
                          fontSize: '14px',
                          fontWeight: '500',
                          display: 'inline-block',
                          transition: 'all 0.2s'
                        }}
                        onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#357abd'}
                        onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#4a90e2'}
                      >
                        {uploading ? '上傳中...' : '選擇圖片'}
                      </label>
                    </>
                  )}
                </div>
              </div>

              <div>
                <label style={{ display: 'block', marginBottom: '10px', fontSize: '16px', fontWeight: '600', color: '#4a5568' }}>
                  📡 發送管道 *
                </label>
                <select
                  value={message.channel}
                  onChange={(e) => setMessage({ ...message, channel: e.target.value })}
                  style={{
                    width: '100%',
                    padding: '12px 16px',
                    border: '2px solid #d1d9e0',
                    borderRadius: '8px',
                    fontSize: '16px',
                    backgroundColor: '#fff',
                    cursor: 'pointer',
                    transition: 'border-color 0.2s',
                    fontWeight: '500'
                  }}
                  onFocus={(e) => e.target.style.borderColor = '#667eea'}
                  onBlur={(e) => e.target.style.borderColor = '#d1d9e0'}
                >
                  <option value="line">💬 LINE</option>
                  <option value="mail">📧 Email</option>
                </select>
              </div>
            </div>

            {/* 優惠券到期提醒 */}
            <div style={{
              background: 'linear-gradient(135deg, #ffffff 0%, #fffbeb 100%)',
              padding: '28px',
              borderRadius: '16px',
              boxShadow: '0 4px 16px rgba(245, 158, 11, 0.1)',
              border: '2px solid #fde68a',
            }}>
              <div style={{
                fontSize: '20px', fontWeight: '700', color: '#92400e',
                marginBottom: '20px', display: 'flex', alignItems: 'center', gap: '8px',
              }}>
                🎟️ 優惠券到期提醒（LINE）
              </div>

              {/* 篩選天數 + 查詢按鈕 */}
              <div style={{ display: 'flex', gap: '12px', alignItems: 'center', marginBottom: '16px', flexWrap: 'wrap' }}>
                <label style={{ fontSize: '15px', fontWeight: '600', color: '#4a5568' }}>到期天數內：</label>
                <select
                  value={couponExpiry.withinDays}
                  onChange={(e) => setCouponExpiry(p => ({ ...p, withinDays: +e.target.value }))}
                  style={{
                    padding: '10px 14px', border: '2px solid #fde68a', borderRadius: '8px',
                    fontSize: '15px', backgroundColor: '#fff', cursor: 'pointer', fontWeight: '600',
                  }}
                >
                  {[3, 5, 7, 14, 30, 60, 90].map(d => (
                    <option key={d} value={d}>{d} 天</option>
                  ))}
                </select>
                <button
                  onClick={loadCouponExpiry}
                  disabled={couponExpiry.loading}
                  style={{
                    padding: '10px 20px', fontSize: '15px', fontWeight: '700', borderRadius: '8px',
                    border: 'none', cursor: couponExpiry.loading ? 'not-allowed' : 'pointer', color: '#fff',
                    background: couponExpiry.loading
                      ? 'linear-gradient(135deg, #cbd5e0 0%, #a0aec0 100%)'
                      : 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)',
                    boxShadow: couponExpiry.loading ? 'none' : '0 4px 12px rgba(245, 158, 11, 0.3)',
                  }}
                >
                  {couponExpiry.loading ? '查詢中…' : '🔍 查詢'}
                </button>
                {couponExpiry.members.length > 0 && (
                  <span style={{ fontSize: '14px', color: '#92400e', fontWeight: '700' }}>
                    共 {couponExpiry.members.length} 位會員有即將到期的券
                  </span>
                )}
              </div>

              {/* 訊息範本編輯 */}
              <div style={{ marginBottom: '16px' }}>
                <label style={{ display: 'block', marginBottom: '8px', fontSize: '15px', fontWeight: '600', color: '#4a5568' }}>
                  訊息範本（{'{coupons}'} 會自動替換為優惠券清單）
                </label>
                <textarea
                  value={couponExpiry.template}
                  onChange={(e) => setCouponExpiry(p => ({ ...p, template: e.target.value }))}
                  style={{
                    width: '100%', minHeight: '80px', padding: '12px', border: '2px solid #fde68a',
                    borderRadius: '8px', fontSize: '15px', fontFamily: 'inherit', resize: 'vertical',
                    boxSizing: 'border-box', lineHeight: '1.6',
                  }}
                />
              </div>

              {/* 會員列表 */}
              {couponExpiry.members.length > 0 && (
                <>
                  <div style={{ display: 'flex', gap: '10px', alignItems: 'center', marginBottom: '12px' }}>
                    <button
                      onClick={toggleCouponAll}
                      style={{
                        padding: '8px 16px', fontSize: '14px', fontWeight: '700', borderRadius: '8px',
                        border: '1px solid #d97706', background: '#fff', color: '#92400e', cursor: 'pointer',
                      }}
                    >
                      {couponExpiry.selected.size === couponExpiry.members.length ? '取消全選' : '全選'}
                    </button>
                    <span style={{ fontSize: '14px', fontWeight: '700', color: '#92400e' }}>
                      已選 {couponExpiry.selected.size} / {couponExpiry.members.length}
                    </span>
                  </div>

                  <div style={{
                    maxHeight: '280px', overflowY: 'auto', border: '1px solid #fde68a',
                    borderRadius: '8px', background: '#fff', marginBottom: '16px',
                  }}>
                    {couponExpiry.members.map(m => (
                      <div
                        key={m.MEMBER_ID}
                        onClick={() => toggleCouponMember(m.MEMBER_ID)}
                        style={{
                          display: 'flex', alignItems: 'flex-start', gap: '10px',
                          padding: '12px 14px', borderBottom: '1px solid #fef3c7', cursor: 'pointer',
                          background: couponExpiry.selected.has(m.MEMBER_ID) ? '#fefce8' : '#fff',
                          transition: 'background 0.15s',
                        }}
                      >
                        <input
                          type="checkbox"
                          checked={couponExpiry.selected.has(m.MEMBER_ID)}
                          onChange={() => toggleCouponMember(m.MEMBER_ID)}
                          style={{ marginTop: '3px', cursor: 'pointer' }}
                        />
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={{ fontWeight: '700', fontSize: '15px', color: '#1a1a1a' }}>
                            {m.NAME}
                            {m.PHONE && <span style={{ marginLeft: 8, fontSize: '13px', color: '#6b7280' }}>{m.PHONE}</span>}
                          </div>
                          <div style={{ marginTop: '4px', display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
                            {m.coupons.map((c, i) => (
                              <span key={i} style={{
                                padding: '3px 10px', borderRadius: '12px', fontSize: '12px', fontWeight: '700',
                                background: c.DAYS_LEFT <= 3
                                  ? 'linear-gradient(135deg, #fee2e2 0%, #fecaca 100%)'
                                  : 'linear-gradient(135deg, #fef3c7 0%, #fde68a 100%)',
                                color: c.DAYS_LEFT <= 3 ? '#991b1b' : '#92400e',
                              }}>
                                {c.COUPON_NAME}（{c.END_DATE}，剩 {c.DAYS_LEFT} 天）
                              </span>
                            ))}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>

                  {/* 預覽 + 發送按鈕 */}
                  <div style={{ display: 'flex', gap: '12px', alignItems: 'center', flexWrap: 'wrap' }}>
                    <button
                      onClick={sendCouponReminder}
                      disabled={couponExpiry.sending || couponExpiry.selected.size === 0}
                      style={{
                        padding: '14px 28px', fontSize: '17px', fontWeight: '700', borderRadius: '10px',
                        border: 'none', cursor: couponExpiry.sending || couponExpiry.selected.size === 0 ? 'not-allowed' : 'pointer',
                        color: '#fff',
                        background: couponExpiry.sending || couponExpiry.selected.size === 0
                          ? 'linear-gradient(135deg, #cbd5e0 0%, #a0aec0 100%)'
                          : 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)',
                        boxShadow: couponExpiry.sending || couponExpiry.selected.size === 0
                          ? 'none'
                          : '0 4px 12px rgba(245, 158, 11, 0.3)',
                      }}
                    >
                      {couponExpiry.sending
                        ? '⏳ 發送中…'
                        : `📤 發送提醒 LINE 給 ${couponExpiry.selected.size} 人`}
                    </button>
                  </div>

                  {couponExpiry.sendResult && (
                    <div style={{
                      marginTop: '14px', padding: '12px 16px', borderRadius: '8px', fontSize: '14px',
                      background: couponExpiry.sendResult.failCount > 0
                        ? 'linear-gradient(135deg, #fff3cd 0%, #fef3c7 100%)'
                        : 'linear-gradient(135deg, #d1fae5 0%, #a7f3d0 100%)',
                      color: couponExpiry.sendResult.failCount > 0 ? '#856404' : '#065f46',
                    }}>
                      發送完成！成功: {couponExpiry.sendResult.successCount}，失敗: {couponExpiry.sendResult.failCount}
                      {couponExpiry.sendResult.noDataCount > 0 && (
                        <span>，無券資料: {couponExpiry.sendResult.noDataCount}</span>
                      )}
                      {couponExpiry.sendResult.failedMembers?.length > 0 && (
                        <div style={{ marginTop: '8px' }}>
                          {couponExpiry.sendResult.failedMembers.map((f, i) => (
                            <div key={i}>{f.name}: {f.error}</div>
                          ))}
                        </div>
                      )}
                    </div>
                  )}
                </>
              )}

              {!couponExpiry.loading && couponExpiry.members.length === 0 && (
                <div style={{ fontSize: '14px', color: '#92400e', opacity: 0.7 }}>
                  請點擊「查詢」搜尋有即將到期優惠券的會員
                </div>
              )}
            </div>

            {/* 指定優惠券未使用持有者提醒 */}
            <div style={{
              background: 'linear-gradient(135deg, #ffffff 0%, #eef2ff 100%)',
              padding: '28px',
              borderRadius: '16px',
              boxShadow: '0 4px 16px rgba(79, 70, 229, 0.12)',
              border: '2px solid #c7d2fe',
              marginTop: '18px',
            }}>
              <div style={{
                fontSize: '20px', fontWeight: '700', color: '#3730a3',
                marginBottom: '20px', display: 'flex', alignItems: 'center', gap: '8px',
              }}>
                🧾 指定優惠券未使用持有者發送（LINE）
              </div>

              <div style={{ display: 'flex', gap: '12px', alignItems: 'center', marginBottom: '16px', flexWrap: 'wrap' }}>
                <label style={{ fontSize: '15px', fontWeight: '600', color: '#4a5568' }}>優惠券：</label>
                <select
                  value={couponHolder.couponId}
                  onChange={(e) => setCouponHolder((p) => ({
                    ...p,
                    couponId: e.target.value,
                    holders: [],
                    selected: new Set(),
                    sendResult: null,
                  }))}
                  style={{
                    minWidth: '260px',
                    padding: '10px 14px', border: '2px solid #c7d2fe', borderRadius: '8px',
                    fontSize: '15px', backgroundColor: '#fff', cursor: 'pointer', fontWeight: '600',
                  }}
                >
                  <option value="">請選擇優惠券</option>
                  {couponHolder.couponOptions.map((c) => (
                    <option key={c.ID} value={c.ID}>{c.NAME}（#{c.ID}）</option>
                  ))}
                </select>
                <button
                  onClick={loadCouponHolders}
                  disabled={couponHolder.loading || !couponHolder.couponId}
                  style={{
                    padding: '10px 20px', fontSize: '15px', fontWeight: '700', borderRadius: '8px',
                    border: 'none', cursor: couponHolder.loading || !couponHolder.couponId ? 'not-allowed' : 'pointer', color: '#fff',
                    background: couponHolder.loading
                      ? 'linear-gradient(135deg, #cbd5e0 0%, #a0aec0 100%)'
                      : 'linear-gradient(135deg, #6366f1 0%, #4f46e5 100%)',
                  }}
                >
                  {couponHolder.loading ? '查詢中…' : '🔍 查詢持有者'}
                </button>
                {couponHolder.holders.length > 0 && (
                  <span style={{ fontSize: '14px', color: '#3730a3', fontWeight: '700' }}>
                    共 {couponHolder.holders.length} 位未使用持有者
                  </span>
                )}
              </div>

              <div style={{ marginBottom: '16px' }}>
                <label style={{ display: 'block', marginBottom: '8px', fontSize: '15px', fontWeight: '600', color: '#4a5568' }}>
                  訊息範本（{'{coupons}'} 會自動替換為優惠券資訊）
                </label>
                <textarea
                  value={couponHolder.template}
                  onChange={(e) => setCouponHolder((p) => ({ ...p, template: e.target.value }))}
                  style={{
                    width: '100%', minHeight: '80px', padding: '12px', border: '2px solid #c7d2fe',
                    borderRadius: '8px', fontSize: '15px', fontFamily: 'inherit', resize: 'vertical',
                    boxSizing: 'border-box', lineHeight: '1.6',
                  }}
                />
              </div>

              {couponHolder.holders.length > 0 && (
                <>
                  <div style={{ display: 'flex', gap: '10px', alignItems: 'center', marginBottom: '12px' }}>
                    <button
                      onClick={toggleCouponHolderAll}
                      style={{
                        padding: '8px 16px', fontSize: '14px', fontWeight: '700', borderRadius: '8px',
                        border: '1px solid #4f46e5', background: '#fff', color: '#3730a3', cursor: 'pointer',
                      }}
                    >
                      {couponHolder.selected.size === couponHolder.holders.length ? '取消全選' : '全選'}
                    </button>
                    <span style={{ fontSize: '14px', fontWeight: '700', color: '#3730a3' }}>
                      已選 {couponHolder.selected.size} / {couponHolder.holders.length}
                    </span>
                  </div>

                  <div style={{
                    maxHeight: '280px', overflowY: 'auto', border: '1px solid #c7d2fe',
                    borderRadius: '8px', background: '#fff', marginBottom: '16px',
                  }}>
                    {couponHolder.holders.map((m) => (
                      <div
                        key={m.MEMBER_ID}
                        onClick={() => toggleCouponHolderMember(m.MEMBER_ID)}
                        style={{
                          display: 'flex', alignItems: 'center', gap: '10px',
                          padding: '12px 14px', borderBottom: '1px solid #e0e7ff', cursor: 'pointer',
                          background: couponHolder.selected.has(m.MEMBER_ID) ? '#eef2ff' : '#fff',
                        }}
                      >
                        <input
                          type="checkbox"
                          checked={couponHolder.selected.has(m.MEMBER_ID)}
                          onChange={() => toggleCouponHolderMember(m.MEMBER_ID)}
                          onClick={(e) => e.stopPropagation()}
                          style={{ marginTop: '2px', cursor: 'pointer' }}
                        />
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={{ fontWeight: '700', fontSize: '15px', color: '#1a1a1a' }}>
                            {m.NAME}
                            {m.PHONE && <span style={{ marginLeft: 8, fontSize: '13px', color: '#6b7280' }}>{m.PHONE}</span>}
                          </div>
                          <div style={{ marginTop: '4px', fontSize: '12px', color: '#4338ca' }}>
                            {m.COUPON_NAME}（未使用 {m.UNUSED_COUNT} 張，{m.END_DATE ? `到期 ${m.END_DATE}` : '無到期日'}）
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>

                  <div style={{ display: 'flex', gap: '12px', alignItems: 'center', flexWrap: 'wrap' }}>
                    <button
                      onClick={sendCouponHolderReminder}
                      disabled={couponHolder.sending || couponHolder.selected.size === 0}
                      style={{
                        padding: '14px 28px', fontSize: '17px', fontWeight: '700', borderRadius: '10px',
                        border: 'none', cursor: couponHolder.sending || couponHolder.selected.size === 0 ? 'not-allowed' : 'pointer',
                        color: '#fff',
                        background: couponHolder.sending || couponHolder.selected.size === 0
                          ? 'linear-gradient(135deg, #cbd5e0 0%, #a0aec0 100%)'
                          : 'linear-gradient(135deg, #6366f1 0%, #4f46e5 100%)',
                      }}
                    >
                      {couponHolder.sending ? '⏳ 發送中…' : `📤 發送提醒 LINE 給 ${couponHolder.selected.size} 人`}
                    </button>
                  </div>

                  {couponHolder.sendResult && (
                    <div style={{
                      marginTop: '14px', padding: '12px 16px', borderRadius: '8px', fontSize: '14px',
                      background: couponHolder.sendResult.failCount > 0
                        ? 'linear-gradient(135deg, #fff3cd 0%, #fef3c7 100%)'
                        : 'linear-gradient(135deg, #d1fae5 0%, #a7f3d0 100%)',
                      color: couponHolder.sendResult.failCount > 0 ? '#856404' : '#065f46',
                    }}>
                      發送完成！成功: {couponHolder.sendResult.successCount}，失敗: {couponHolder.sendResult.failCount}
                      {couponHolder.sendResult.noDataCount > 0 && (
                        <span>，無券資料: {couponHolder.sendResult.noDataCount}</span>
                      )}
                      {couponHolder.sendResult.failedMembers?.length > 0 && (
                        <div style={{ marginTop: '8px' }}>
                          {couponHolder.sendResult.failedMembers.map((f, i) => (
                            <div key={i}>{f.name}: {f.error}</div>
                          ))}
                        </div>
                      )}
                    </div>
                  )}
                </>
              )}

              {!couponHolder.loading && couponHolder.holders.length === 0 && (
                <div style={{ fontSize: '14px', color: '#3730a3', opacity: 0.7 }}>
                  請先選擇優惠券並點擊「查詢持有者」
                </div>
              )}
            </div>

            {/* 預發瀏覽與正式發送 */}
            <div style={{
              background: 'linear-gradient(135deg, #ffffff 0%, #fff5f7 100%)',
              padding: '28px',
              paddingBottom: '32px',
              borderRadius: '16px',
              boxShadow: '0 4px 16px rgba(245, 87, 108, 0.1)',
              border: '2px solid #fed7e2',
              marginBottom: '20px'
            }}>
              <div style={{ 
                fontSize: '20px', 
                fontWeight: '700', 
                color: '#c53030',
                marginBottom: '24px',
                display: 'flex',
                alignItems: 'center',
                gap: '8px'
              }}>
                🚀 預發瀏覽與正式發送
              </div>
              
              <div style={{ marginBottom: '24px', position: 'relative' }}>
                <label style={{ display: 'block', marginBottom: '10px', fontSize: '16px', fontWeight: '600', color: '#4a5568' }}>
                  👤 選擇預發對象（輸入名稱搜尋）
                </label>
                <div style={{ position: 'relative' }}>
                  <input
                    ref={recipientInputRef}
                    type="text"
                    value={preview.searchText}
                    onChange={(e) => searchRecipient(e.target.value)}
                    onFocus={() => {
                      if (preview.searchText) {
                        searchRecipient(preview.searchText);
                      } else {
                        // 預設顯示員工列表
                        setRecipientSearchResults(staffList.map(s => ({ ...s, type: 'staff' })));
                        setShowRecipientDropdown(true);
                      }
                    }}
                    placeholder="輸入員工或會員名稱進行搜尋..."
                    style={{
                      width: '100%',
                      padding: '12px 16px',
                      border: '2px solid #d1d9e0',
                      borderRadius: '8px',
                      fontSize: '16px',
                      boxSizing: 'border-box',
                      paddingRight: '40px',
                      transition: 'border-color 0.2s'
                    }}
                    onFocus={(e) => e.target.style.borderColor = '#667eea'}
                    onBlur={(e) => e.target.style.borderColor = '#d1d9e0'}
                  />
                  {preview.recipientId && (
                    <button
                      onClick={() => {
                        setPreview({ ...preview, recipientId: '', recipientName: '', searchText: '' });
                        setShowRecipientDropdown(false);
                      }}
                      style={{
                        position: 'absolute',
                        right: '8px',
                        top: '50%',
                        transform: 'translateY(-50%)',
                        backgroundColor: '#dc3545',
                        color: '#fff',
                        border: 'none',
                        borderRadius: '4px',
                        width: '24px',
                        height: '24px',
                        cursor: 'pointer',
                        fontSize: '16px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        lineHeight: 1
                      }}
                    >
                      ×
                    </button>
                  )}
                  {showRecipientDropdown && recipientSearchResults.length > 0 && (
                    <div
                      ref={recipientDropdownRef}
                      style={{
                        position: 'absolute',
                        top: '100%',
                        left: 0,
                        right: 0,
                        backgroundColor: '#fff',
                        border: '1px solid #d1d9e0',
                        borderRadius: '6px',
                        maxHeight: '250px',
                        overflowY: 'auto',
                        zIndex: 1000,
                        boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                        marginTop: '4px'
                      }}
                    >
                      {recipientSearchResults.map(recipient => (
                        <div
                          key={`${recipient.type}-${recipient.ID}`}
                          onClick={() => selectRecipient(recipient)}
                          style={{
                            padding: '12px 16px',
                            cursor: 'pointer',
                            borderBottom: '1px solid #f0f0f0',
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            transition: 'background-color 0.2s'
                          }}
                          onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#f8fbff'}
                          onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#fff'}
                        >
                          <div>
                            <div style={{ fontWeight: '500', fontSize: '14px', color: '#1a1a1a' }}>
                              {recipient.NAME}
                            </div>
                            <div style={{ fontSize: '12px', color: '#8a9ba8', marginTop: '2px' }}>
                              {recipient.type === 'staff' ? '👤 員工' : '👥 會員'}
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
                {preview.recipientName && (
                  <div style={{ 
                    marginTop: '10px', 
                    fontSize: '15px', 
                    color: '#059669', 
                    fontWeight: '600',
                    padding: '8px 12px',
                    backgroundColor: '#d1fae5',
                    borderRadius: '8px',
                    display: 'inline-block'
                  }}>
                    ✅ 已選擇: {preview.recipientName} ({preview.recipientType === 'staff' ? '員工' : '會員'})
                  </div>
                )}
              </div>

              <div style={{ 
                display: 'flex', 
                gap: '12px',
                position: 'sticky',
                bottom: 0,
                backgroundColor: 'transparent',
                paddingTop: '20px',
                marginTop: 'auto',
                zIndex: 10
              }}>
                <button
                  onClick={handleTestSend}
                  disabled={testing || !preview.recipientId || !message.content}
                  style={{
                    flex: 1,
                    padding: '14px 28px',
                    background: testing || !preview.recipientId || !message.content 
                      ? 'linear-gradient(135deg, #cbd5e0 0%, #a0aec0 100%)'
                      : 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                    color: '#fff',
                    border: 'none',
                    borderRadius: '10px',
                    fontSize: '17px',
                    fontWeight: '600',
                    cursor: testing || !preview.recipientId || !message.content ? 'not-allowed' : 'pointer',
                    transition: 'all 0.2s',
                    boxShadow: testing || !preview.recipientId || !message.content 
                      ? 'none'
                      : '0 4px 12px rgba(102, 126, 234, 0.3)'
                  }}
                  onMouseEnter={(e) => {
                    if (!testing && preview.recipientId && message.content) {
                      e.currentTarget.style.transform = 'translateY(-2px)';
                      e.currentTarget.style.boxShadow = '0 6px 16px rgba(102, 126, 234, 0.4)';
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (!testing && preview.recipientId && message.content) {
                      e.currentTarget.style.transform = 'translateY(0)';
                      e.currentTarget.style.boxShadow = '0 4px 12px rgba(102, 126, 234, 0.3)';
                    }
                  }}
                >
                  {testing ? '⏳ 測試發送中...' : '🧪 測試發送'}
                </button>

                <button
                  onClick={handleSend}
                  disabled={sending || selectedMembers.size === 0 || !message.content}
                  style={{
                    flex: 1,
                    padding: '14px 28px',
                    background: sending || selectedMembers.size === 0 || !message.content
                      ? 'linear-gradient(135deg, #cbd5e0 0%, #a0aec0 100%)'
                      : 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
                    color: '#fff',
                    border: 'none',
                    borderRadius: '10px',
                    fontSize: '17px',
                    fontWeight: '600',
                    cursor: sending || selectedMembers.size === 0 || !message.content ? 'not-allowed' : 'pointer',
                    transition: 'all 0.2s',
                    boxShadow: sending || selectedMembers.size === 0 || !message.content
                      ? 'none'
                      : '0 4px 12px rgba(245, 87, 108, 0.3)'
                  }}
                  onMouseEnter={(e) => {
                    if (!sending && selectedMembers.size > 0 && message.content) {
                      e.currentTarget.style.transform = 'translateY(-2px)';
                      e.currentTarget.style.boxShadow = '0 6px 16px rgba(245, 87, 108, 0.4)';
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (!sending && selectedMembers.size > 0 && message.content) {
                      e.currentTarget.style.transform = 'translateY(0)';
                      e.currentTarget.style.boxShadow = '0 4px 12px rgba(245, 87, 108, 0.3)';
                    }
                  }}
                >
                  {sending ? '⏳ 發送中...' : `🚀 正式發送 (${selectedMembers.size}人)`}
                </button>
              </div>

              {testResult && (
                <div style={{
                  marginTop: '16px',
                  padding: '12px 16px',
                  backgroundColor: testResult.success ? '#d4edda' : '#f8d7da',
                  borderRadius: '6px',
                  fontSize: '14px',
                  color: testResult.success ? '#155724' : '#721c24'
                }}>
                  {testResult.success ? '✅ 測試發送成功' : `❌ 測試發送失敗: ${testResult.error}`}
                </div>
              )}

              {sendResult && (
                <div style={{ marginTop: '16px' }}>
                  <div style={{
                    padding: '12px 16px',
                    backgroundColor: '#d4edda',
                    borderRadius: '6px',
                    marginBottom: '12px',
                    fontSize: '14px',
                    color: '#155724'
                  }}>
                    <strong>發送完成！</strong><br />
                    總數: {sendResult.totalRecipients} | 
                    成功: {sendResult.successCount} | 
                    失敗: {sendResult.failCount}
                  </div>

                  {sendResult.failedMembers && sendResult.failedMembers.length > 0 && (
                    <div style={{
                      padding: '12px 16px',
                      backgroundColor: '#fff3cd',
                      borderRadius: '6px',
                      maxHeight: '200px',
                      overflowY: 'auto',
                      fontSize: '13px'
                    }}>
                      <strong>失敗名單：</strong>
                      {sendResult.failedMembers.map((m, i) => (
                        <div key={i} style={{ marginTop: '8px', color: '#856404' }}>
                          {m.name} - {m.error}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        ) : (
          /* 歷史記錄 */
          <div style={{
            backgroundColor: '#fff',
            padding: '24px',
            borderRadius: '12px',
            boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
            border: '1px solid #e1e8ed'
          }}>
            <div style={{ fontSize: '18px', fontWeight: '600', color: '#1a1a1a', marginBottom: '20px' }}>
              發送歷史
            </div>
            <div style={{ overflowX: 'auto', WebkitOverflowScrolling: 'touch', maxWidth: '100%' }}>
            <table style={{ width: '100%', minWidth: 720, borderCollapse: 'collapse' }}>
              <thead>
                <tr>
                  <th style={{ padding: '12px', textAlign: 'left', borderBottom: '2px solid #e1e8ed', backgroundColor: '#fafbfc', fontWeight: '600', fontSize: '14px' }}>時間</th>
                  <th style={{ padding: '12px', textAlign: 'left', borderBottom: '2px solid #e1e8ed', backgroundColor: '#fafbfc', fontWeight: '600', fontSize: '14px' }}>標題</th>
                  <th style={{ padding: '12px', textAlign: 'left', borderBottom: '2px solid #e1e8ed', backgroundColor: '#fafbfc', fontWeight: '600', fontSize: '14px' }}>管道</th>
                  <th style={{ padding: '12px', textAlign: 'left', borderBottom: '2px solid #e1e8ed', backgroundColor: '#fafbfc', fontWeight: '600', fontSize: '14px' }}>總數</th>
                  <th style={{ padding: '12px', textAlign: 'left', borderBottom: '2px solid #e1e8ed', backgroundColor: '#fafbfc', fontWeight: '600', fontSize: '14px' }}>成功</th>
                  <th style={{ padding: '12px', textAlign: 'left', borderBottom: '2px solid #e1e8ed', backgroundColor: '#fafbfc', fontWeight: '600', fontSize: '14px' }}>失敗</th>
                  <th style={{ padding: '12px', textAlign: 'left', borderBottom: '2px solid #e1e8ed', backgroundColor: '#fafbfc', fontWeight: '600', fontSize: '14px' }}>操作</th>
                </tr>
              </thead>
              <tbody>
                {history.map(campaign => (
                  <tr key={campaign.ID}>
                    <td style={{ padding: '12px', borderBottom: '1px solid #e1e8ed', fontSize: '14px' }}>
                      {new Date(campaign.SENT_AT || campaign.CREATED_AT).toLocaleString('zh-TW')}
                    </td>
                    <td style={{ padding: '12px', borderBottom: '1px solid #e1e8ed', fontSize: '14px' }}>
                      {campaign.TITLE || '(無標題)'}
                    </td>
                    <td style={{ padding: '12px', borderBottom: '1px solid #e1e8ed', fontSize: '14px' }}>
                      {campaign.CHANNEL === 'line' ? 'LINE' : 'Email'}
                    </td>
                    <td style={{ padding: '12px', borderBottom: '1px solid #e1e8ed', fontSize: '14px' }}>
                      {campaign.TOTAL_RECIPIENTS}
                    </td>
                    <td style={{ padding: '12px', borderBottom: '1px solid #e1e8ed', fontSize: '14px', color: '#28a745' }}>
                      {campaign.SUCCESS_COUNT}
                    </td>
                    <td style={{ padding: '12px', borderBottom: '1px solid #e1e8ed', fontSize: '14px', color: '#dc3545' }}>
                      {campaign.FAIL_COUNT}
                    </td>
                    <td style={{ padding: '12px', borderBottom: '1px solid #e1e8ed', fontSize: '14px' }}>
                      <button
                        onClick={() => viewCampaignDetail(campaign.ID)}
                        style={{
                          padding: '6px 12px',
                          backgroundColor: '#6c757d',
                          color: '#fff',
                          border: 'none',
                          borderRadius: '4px',
                          fontSize: '12px',
                          cursor: 'pointer'
                        }}
                      >
                        查看詳情
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            </div>

            {selectedCampaign && (
              <div style={{ marginTop: '24px', padding: '20px', backgroundColor: '#fafbfc', borderRadius: '8px', border: '1px solid #e1e8ed' }}>
                <h3 style={{ marginTop: 0, marginBottom: '16px', fontSize: '16px', fontWeight: '600' }}>活動詳情</h3>
                <div style={{ marginBottom: '12px', fontSize: '14px' }}>
                  <strong>標題：</strong> {selectedCampaign.campaign.TITLE || '(無標題)'}
                </div>
                <div style={{ marginBottom: '12px', fontSize: '14px' }}>
                  <strong>內容：</strong> {selectedCampaign.campaign.CONTENT}
                </div>
                <div style={{ marginBottom: '12px', fontSize: '14px' }}>
                  <strong>發送時間：</strong> {new Date(selectedCampaign.campaign.SENT_AT).toLocaleString('zh-TW')}
                </div>
                <div style={{ marginBottom: '12px', fontSize: '14px', fontWeight: '600' }}>
                  接收者名單：
                </div>
                <div style={{ maxHeight: '300px', overflow: 'auto', WebkitOverflowScrolling: 'touch', maxWidth: '100%' }}>
                  <table style={{ width: '100%', minWidth: 640, borderCollapse: 'collapse', fontSize: '13px' }}>
                    <thead>
                      <tr>
                        <th style={{ padding: '8px', textAlign: 'left', borderBottom: '2px solid #e1e8ed', backgroundColor: '#fafbfc', fontWeight: '600' }}>姓名</th>
                        <th style={{ padding: '8px', textAlign: 'left', borderBottom: '2px solid #e1e8ed', backgroundColor: '#fafbfc', fontWeight: '600' }}>Email</th>
                        <th style={{ padding: '8px', textAlign: 'left', borderBottom: '2px solid #e1e8ed', backgroundColor: '#fafbfc', fontWeight: '600' }}>電話</th>
                        <th style={{ padding: '8px', textAlign: 'left', borderBottom: '2px solid #e1e8ed', backgroundColor: '#fafbfc', fontWeight: '600' }}>狀態</th>
                        <th style={{ padding: '8px', textAlign: 'left', borderBottom: '2px solid #e1e8ed', backgroundColor: '#fafbfc', fontWeight: '600' }}>錯誤訊息</th>
                      </tr>
                    </thead>
                    <tbody>
                      {selectedCampaign.recipients.map((r, i) => (
                        <tr key={i}>
                          <td style={{ padding: '8px', borderBottom: '1px solid #e1e8ed' }}>{r.MEMBER_NAME}</td>
                          <td style={{ padding: '8px', borderBottom: '1px solid #e1e8ed' }}>{r.MEMBER_EMAIL || '-'}</td>
                          <td style={{ padding: '8px', borderBottom: '1px solid #e1e8ed' }}>{r.MEMBER_PHONE || '-'}</td>
                          <td style={{ padding: '8px', borderBottom: '1px solid #e1e8ed', color: r.STATUS === 'success' ? '#28a745' : '#dc3545' }}>
                            {r.STATUS === 'success' ? '成功' : '失敗'}
                          </td>
                          <td style={{ padding: '8px', borderBottom: '1px solid #e1e8ed' }}>{r.ERROR_MESSAGE || '-'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <button
                  onClick={() => setSelectedCampaign(null)}
                  style={{
                    marginTop: '16px',
                    padding: '8px 16px',
                    backgroundColor: '#6c757d',
                    color: '#fff',
                    border: 'none',
                    borderRadius: '6px',
                    fontSize: '14px',
                    cursor: 'pointer'
                  }}
                >
                  關閉
                </button>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
